import { takeLatest, put } from "redux-saga/effects";
import { ToastError, ToastSuccess, ToastInfo } from "components/Global/Toasts/Toasty";
import { LOAN_STATUSES } from "@loanbossinc/component-library";
import {
  defaultReturn
  , multipleObjectReturn
  , makePayload
  , generateDefaultActions
  , requestSaga
  , handleCallback
} from "shared-utility/store/utils/helpers";

// ACTIONS

export const DEAL_ANALYSIS_GET = generateDefaultActions("DEAL_ANALYSIS_GET");
export const dealGet = payload => makePayload(DEAL_ANALYSIS_GET.value, payload);
function* dealGetSaga(payload) {
  const url = `/deals/${payload.dealId}`;
  yield requestSaga(DEAL_ANALYSIS_GET, url, "GET");
}

export const DEAL_ANALYSIS_GET_DEAL_METRICS = generateDefaultActions("DEAL_ANALYSIS_GET_DEAL_METRICS");
export const dealAnalysisGetDealMetrics = payload => makePayload(DEAL_ANALYSIS_GET_DEAL_METRICS.value, payload);
function* dealAnalysisGetDealMetricsSaga(payload) {
  const url = `/deals/${payload.dealId}/metrics`;
  yield requestSaga(DEAL_ANALYSIS_GET_DEAL_METRICS, url, "GET");
}

export const DEAL_ANALYSIS_GET_CLOSED_LOANS = generateDefaultActions("DEAL_ANALYSIS_GET_CLOSED_LOANS");
export const dealAnalysisGetClosedLoans = payload => makePayload(DEAL_ANALYSIS_GET_CLOSED_LOANS.value, payload);
function* dealAnalysisGetClosedLoansSaga(payload) {
  const url = `/loans/getLoansByDealIdAndStatus/${payload.dealId}/${LOAN_STATUSES.CLOSED_LOAN}`;
  yield requestSaga(DEAL_ANALYSIS_GET_CLOSED_LOANS, url, "GET");
}

export const DEAL_ANALYSIS_GET_DEAL_PROPERTY_PROJECTIONS = generateDefaultActions("DEAL_ANALYSIS_GET_DEAL_PROPERTY_PROJECTIONS");
export const getDealPropertyProjections = payload => makePayload(DEAL_ANALYSIS_GET_DEAL_PROPERTY_PROJECTIONS.value, payload);
function* getDealPropertyProjectionsSaga(payload) {
  const url = `/getDealPropertyProjections/${payload.dealId}`;
  yield requestSaga(DEAL_ANALYSIS_GET_DEAL_PROPERTY_PROJECTIONS, url);
}

export const GET_REFI_CALC_RESULTS = generateDefaultActions("GET_REFI_CALC_RESULTS");
export const getRefiCalcResults = payload => makePayload(GET_REFI_CALC_RESULTS.value, payload);
function* getRefiCalcResultsSaga({ dealId }) {
  const url = `/loanProceeds/results/${dealId}`;
  yield requestSaga(GET_REFI_CALC_RESULTS, url, "GET");
}

export const GET_REFI_SCENARIO = generateDefaultActions("GET_REFI_SCENARIO");
export const getRefiScenario = payload => makePayload(GET_REFI_SCENARIO.value, payload);
function* getRefiScenarioSaga({ dealId, loanSpreadId }) {
  const url = `/loanProceeds/scenario/${dealId}/${loanSpreadId}`;
  yield requestSaga(GET_REFI_SCENARIO, url, "GET");
}

export const FAVORITE_SCENARIO = generateDefaultActions("FAVORITE_SCENARIO");
export const favoriteScenario = payload => makePayload(FAVORITE_SCENARIO.value, payload);
function* favoriteScenarioSaga({ dealId, loanSpreadId, noi, capRate }) {
  const url = `/loanProceeds/scenario/${dealId}/${loanSpreadId}/favorite`;

  const response = yield requestSaga(FAVORITE_SCENARIO, url, "POST", { noi, capRate });
  if (response.error) {
    yield put(FAVORITE_SCENARIO.createFailure({ error: "fail" }));
  } else {
    yield getRefiScenarioSaga({ dealId, loanSpreadId });
    yield put(FAVORITE_SCENARIO.createSuccess());
  }
}

export const DEFAVORITE_SCENARIO = generateDefaultActions("DEFAVORITE_SCENARIO");
export const defavoriteScenario = payload => makePayload(DEFAVORITE_SCENARIO.value, payload);
function* defavoriteScenarioSaga({ dealId, loanSpreadId }) {
  const url = `/loanProceeds/scenario/${dealId}/${loanSpreadId}/favorite`;
  const response = yield requestSaga(DEFAVORITE_SCENARIO, url, "DELETE");

  if (response.error) {
    yield put(DEFAVORITE_SCENARIO.createFailure({ error: "fail" }));
  } else {
    yield getRefiScenarioSaga({ dealId, loanSpreadId });
    yield put(DEFAVORITE_SCENARIO.createSuccess());
  }
}

export const CASHOUT_ONE_OFF_CALC = generateDefaultActions("CASHOUT_ONE_OFF_CALC");
export const cashoutOneOffCalc = payload => makePayload(CASHOUT_ONE_OFF_CALC.value, payload);
function* cashoutOneOffCalcSaga(payload) {
  const { dealId, loanSpreadId, params } = payload;
  const callback = handleCallback(payload);
  const url = `/loanProceeds/scenario/${dealId}/${loanSpreadId}`;
  ToastInfo("Calculating Refi Proceeds Analysis");
  yield requestSaga(CASHOUT_ONE_OFF_CALC, url, "POST", { params });
  yield callback();
}

export const CASHOUT_RECALC_FOR_DEAL = generateDefaultActions("CASHOUT_RECALC_FOR_DEAL");
export const cashoutReCalcForDeal = payload => makePayload(CASHOUT_RECALC_FOR_DEAL.value, payload);
function* cashoutReCalcForDealSaga({ dealId }) {
  // /recalculate
  const url = `/loanProceeds/recalculate/deals/${dealId}/`;
  yield requestSaga(CASHOUT_RECALC_FOR_DEAL, url, "POST");
}

export const FAVORITE_TERM = generateDefaultActions("FAVORITE_TERM");
export const favoriteTerm = payload => makePayload(FAVORITE_TERM.value, payload);
function* favoriteTermSaga({ dealId, term }) {
  const url = `/loanProceeds/results/${dealId}/${term}/favorite`;
  const response = yield requestSaga(FAVORITE_TERM, url, "POST");
  if (response.error) {
    yield put(FAVORITE_TERM.createFailure({ error: "fail" }));
  } else {
    yield getRefiCalcResultsSaga({ dealId });
    yield put(FAVORITE_TERM.createSuccess());
  }
}

// REDUCER
const initialState = {
  deal: null,
  closedLoans: null,
  dealPropertyProjections: null,
  refiCalcResults: {
    refiResults: [],
    metrics: {
      noi: null,
      occupancy: null,
      marketValue: null,
      capRate: null
    }
  },
  calculationResults: [],
  refiScenario: {},
  dealRecalc: {}
};

export function dealAnalysisReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case DEAL_ANALYSIS_GET.error:
      ToastError("Data Fetch Unsuccessful");

      return defaultReturn(state);

    case DEAL_ANALYSIS_GET.success:
      return multipleObjectReturn(state, ["currentdeal", "guestPermission"], [payload.deal, payload.guestPermission]);

    case DEAL_ANALYSIS_GET_DEAL_METRICS.success:
      return defaultReturn(state, payload.data, "deal");

    case DEAL_ANALYSIS_GET_CLOSED_LOANS.success:
      return defaultReturn(state, payload.loans, "closedLoans");

    case DEAL_ANALYSIS_GET_DEAL_PROPERTY_PROJECTIONS.success:
      const consolidatedProjections = payload && payload.result && payload.result.consolidatedProjections ? payload.result.consolidatedProjections : {};

      return defaultReturn(state, consolidatedProjections, "dealPropertyProjections");

    case GET_REFI_CALC_RESULTS.success:
      return defaultReturn(state, payload.data, "refiCalcResults");

    case GET_REFI_SCENARIO.success:
      return defaultReturn(state, payload.data, "refiScenario");

    case FAVORITE_SCENARIO.success:
      ToastSuccess("Scenario Favoriting Success");

      return defaultReturn(state, payload.data);

    case DEFAVORITE_SCENARIO.success:
      ToastSuccess("Scenario DeFavoriting Success");

      return defaultReturn(state, payload.data);

    case FAVORITE_SCENARIO.failure:
      ToastError("Scenario Favoriting Failed");

      return defaultReturn(state, payload.data);

    case DEFAVORITE_SCENARIO.failure:
      ToastError("Scenario DeFavoriting Failed");

      return defaultReturn(state, payload.data);

    case CASHOUT_ONE_OFF_CALC.success:
      ToastSuccess("Cashout Calculation Successful");

      return defaultReturn(state, payload.data, "calculationResults");

    case CASHOUT_ONE_OFF_CALC.failure:
      ToastError("Cashout Calculation Failed");

      return defaultReturn(state, payload.data, "calculationResults");

    case CASHOUT_RECALC_FOR_DEAL.success:
      ToastSuccess("Starting recalculation for deal");

      return defaultReturn(state, payload.data, "dealRecalc");

    case CASHOUT_RECALC_FOR_DEAL.failure:
      ToastError("Failed to start recalculation for deal");

      return defaultReturn(state, payload.data, "dealRecalc");

    default:
      return state;
  }
}

// SAGA

export function* dealAnalysisSaga() {
  yield takeLatest(DEAL_ANALYSIS_GET_DEAL_PROPERTY_PROJECTIONS.value, getDealPropertyProjectionsSaga);
  yield takeLatest(DEAL_ANALYSIS_GET_DEAL_METRICS.value, dealAnalysisGetDealMetricsSaga);
  yield takeLatest(DEAL_ANALYSIS_GET_CLOSED_LOANS.value, dealAnalysisGetClosedLoansSaga);
  yield takeLatest(DEAL_ANALYSIS_GET.value, dealGetSaga);
  yield takeLatest(GET_REFI_CALC_RESULTS.value, getRefiCalcResultsSaga);
  yield takeLatest(GET_REFI_SCENARIO.value, getRefiScenarioSaga);
  yield takeLatest(FAVORITE_SCENARIO.value, favoriteScenarioSaga);
  yield takeLatest(DEFAVORITE_SCENARIO.value, defavoriteScenarioSaga);
  yield takeLatest(CASHOUT_ONE_OFF_CALC.value, cashoutOneOffCalcSaga);
  yield takeLatest(CASHOUT_RECALC_FOR_DEAL.value, cashoutReCalcForDealSaga);
  yield takeLatest(FAVORITE_TERM.value, favoriteTermSaga);
}
